import type { MessageDescriptor } from 'react-intl';
import { defineMessages } from 'react-intl';

import { Providers } from '../enums/providers';
import { FeatureFlagDeclarations } from '../feature-flags/feature-flag-declarations';

/**
 * DDP Country Configuration
 *
 * Central configuration for Delivered Duty Paid (DDP) functionality per country.
 *
 * Architecture decisions:
 * 1. Default messages with per-country overrides - reduces duplication, ensures fallback
 * 2. Carriers built from byoaCarriers + walletedCarriers - single source of truth for walleted
 * 3. Helper function createCountryConfig - ensures consistent structure
 */

// ============================================
// Types
// ============================================

type FeatureFlagDeclaration = {
  key: string;
  defaultValue: boolean;
};

type DdpIntroductionMessages = {
  modalTitle: MessageDescriptor;
  modalDescription: MessageDescriptor;
  acceptTermsButton: MessageDescriptor;
  remindMeLaterLink: MessageDescriptor;
  acceptErrorTitle: MessageDescriptor;
  acceptError: MessageDescriptor;
};

type DdpTermsMessages = {
  modalTitle: MessageDescriptor;
  mainTextParagraph1: MessageDescriptor;
  mainTextParagraph2: MessageDescriptor;
  checkboxLabel: MessageDescriptor;
  termsLinkText: MessageDescriptor;
  acceptButton: MessageDescriptor;
  cancelButton: MessageDescriptor;
  remindMeLaterButton: MessageDescriptor;
  errorTitle: MessageDescriptor;
  acceptError: MessageDescriptor;
};

export type DdpCountryConfig = {
  countryCode: string;
  /**
   * Feature flag controlling DDP availability for this country.
   * - If provided: DDP availability depends on LaunchDarkly flag state
   * - If omitted: DDP is always enabled for this country (flag removed after full rollout)
   */
  featureFlag?: FeatureFlagDeclaration;
  carriers: string[];
  walletedCarriers: string[];
  termsLabel: string;
  snoozeDurationHours: number;
  introductionMessages: DdpIntroductionMessages;
  termsMessages: DdpTermsMessages;
};

type CountryConfigInput = {
  countryCode: string;
  /**
   * Feature flag controlling DDP availability.
   * Omit this field to make DDP always enabled (after feature flag cleanup).
   */
  featureFlag?: FeatureFlagDeclaration;
  byoaCarriers: string[];
  walletedCarriers: string[];
  termsLabel: string;
  snoozeDurationHours?: number;
  introductionOverrides?: Partial<DdpIntroductionMessages>;
  termsOverrides?: Partial<DdpTermsMessages>;
};

// ============================================
// Constants
// ============================================

export const BASE_TERMS_URL = 'https://www.shipstation.com/terms-of-service/';
export const DDP_MODAL_SNOOZE_KEY = 'ddpModalSnoozedUntilTimestamp';
export const DEFAULT_SNOOZE_DURATION_HOURS = 24;

// ============================================
// Default Messages (shared across countries)
// ============================================

export const defaultIntroductionMessages: DdpIntroductionMessages = defineMessages({
  modalTitle: {
    id: 'qO3alV',
    description: 'Title for DDP introduction modal',
    defaultMessage: 'Introducing Guaranteed Prepaid Duties and Taxes',
  },
  modalDescription: {
    id: '7LK4l/',
    description: 'Description text for DDP introduction modal',
    defaultMessage:
      'ShipStation customers of all sizes can get a <link>guaranteed cost for duties and taxes</link> at label purchase. Click accept to set it up now.',
  },
  acceptTermsButton: {
    id: 'o70UN+',
    description: 'Button text to accept DDP terms',
    defaultMessage: 'Accept Terms',
  },
  remindMeLaterLink: {
    id: 'LjMHjz',
    description: 'Link text to remind user later about DDP',
    defaultMessage: 'Remind Me Later',
  },
  acceptErrorTitle: {
    id: '7iNwdB',
    description: 'Error message title when accepting DDP terms fails',
    defaultMessage: 'Error',
  },
  acceptError: {
    id: 'NtvOq+',
    description: 'Error message when accepting DDP terms fails',
    defaultMessage: 'There was an error accepting the terms. Please try again.',
  },
});

export const defaultTermsMessages: DdpTermsMessages = defineMessages({
  modalTitle: {
    id: 'eVDuYR',
    description: 'Title for DDP Terms and Conditions modal',
    defaultMessage: 'Duties and Taxes – Terms and Conditions',
  },
  mainTextParagraph1: {
    id: 'Jjf+JO',
    description: 'First paragraph of DDP Terms modal',
    defaultMessage:
      '"Guaranteed Prepaid Duties and Taxes" is an optional new feature that allows ShipStation customers to pay a guaranteed amount for duties and taxes at the time of a Delivered Duty Paid (DDP) label purchase.',
  },
  mainTextParagraph2: {
    id: 'LUVylF',
    description: 'Second paragraph of DDP Terms modal',
    defaultMessage:
      'Accept the terms to use this service for supported carriers by ShipStation. Accepting does not require use of this service. Delivered Duty Unpaid (DDU) services are still available.',
  },
  checkboxLabel: {
    id: '9FLjlT',
    description: 'Checkbox label for accepting DDP terms',
    defaultMessage: 'I accept the Guaranteed Duties and Taxes Feature terms.',
  },
  termsLinkText: {
    id: '7OzBq8',
    description: 'Link text to view Terms and Conditions',
    defaultMessage: 'View Terms and Conditions',
  },
  acceptButton: {
    id: '0Bk3Vl',
    description: 'Accept button text for DDP terms modal',
    defaultMessage: 'Accept',
  },
  cancelButton: {
    id: 'Tbud/k',
    description: 'Cancel button text for DDP terms modal',
    defaultMessage: 'Cancel',
  },
  remindMeLaterButton: {
    id: 'o0hzyP',
    description: 'Remind me later button text for DDP terms modal',
    defaultMessage: 'Remind Me Later',
  },
  errorTitle: {
    id: 'AFDzEA',
    description: 'Error title when accepting DDP terms fails',
    defaultMessage: 'Error',
  },
  acceptError: {
    id: 'NtvOq+',
    description: 'Error message when accepting DDP terms fails',
    defaultMessage: 'There was an error accepting the terms. Please try again.',
  },
});

// ============================================
// Per-Country Message Overrides
// Only define messages that differ from defaults
// ============================================

// ============================================
// Helper Function to Build Country Config
// ============================================

/**
 * Creates a country configuration with carriers built from byoa + walleted
 * This ensures walleted carriers are always included in the carriers array
 *
 * Feature flag handling:
 * - If featureFlag is provided: DDP availability controlled by LaunchDarkly
 * - If featureFlag is omitted: DDP is always enabled (for post-rollout cleanup)
 */
const createCountryConfig = ({
  countryCode,
  featureFlag,
  byoaCarriers,
  walletedCarriers,
  termsLabel,
  snoozeDurationHours = DEFAULT_SNOOZE_DURATION_HOURS,
  introductionOverrides = {},
  termsOverrides = {},
}: CountryConfigInput): DdpCountryConfig => ({
  countryCode,
  // Only include featureFlag if provided (omitted = always enabled)
  ...(featureFlag && { featureFlag }),
  // carriers = byoaCarriers + walletedCarriers (single source of truth)
  carriers: [...byoaCarriers, ...walletedCarriers],
  walletedCarriers,
  termsLabel,
  snoozeDurationHours,
  // Merge default messages with country-specific overrides
  introductionMessages: { ...defaultIntroductionMessages, ...introductionOverrides },
  termsMessages: { ...defaultTermsMessages, ...termsOverrides },
});

// ============================================
// Country Configuration Map
// ============================================

export const DDP_COUNTRY_CONFIG: Record<string, DdpCountryConfig> = {
  US: createCountryConfig({
    countryCode: 'US',
    featureFlag: FeatureFlagDeclarations.enablePrepaidDutiesAndTaxUs,
    byoaCarriers: [
      Providers.UPS.labelProviderId, // '3'
      Providers.FedEx.labelProviderId, // '4'
      Providers.DhlExpress.labelProviderId, // '13'
    ],
    walletedCarriers: [
      Providers.UPSWallet.labelProviderId, // '103'
      Providers.FedExWalleted.labelProviderId, // '194'
      Providers.DhlExpressByShipStation.labelProviderId, // '38'
    ],
    termsLabel: 'duties_and_taxes_2025',
    // US uses default messages - no overrides needed
  }),
  CA: createCountryConfig({
    countryCode: 'CA',
    featureFlag: FeatureFlagDeclarations.enablePrepaidDutiesAndTaxCa,
    // Phase 1: UPS only (BYOA + Walleted)
    byoaCarriers: [
      Providers.UPS.labelProviderId, // '3'
      // Phase 2: Providers.FedEx.labelProviderId,        // '4'
      // Phase 3: Providers.DhlExpress.labelProviderId,   // '13'
    ],
    walletedCarriers: [
      Providers.UPSWallet.labelProviderId, // '103'
      // Phase 2: Providers.FedExWalleted.labelProviderId,                // '194'
      // Phase 3: Providers.DhlExpressByShipStation.labelProviderId,      // '38'
    ],
    termsLabel: 'duties_and_taxes_ca_2026',
  }),
};

// ============================================
// Helper Functions
// ============================================

/**
 * Get DDP configuration for a specific country
 */
export const getDdpCountryConfig = (countryCode: string | null | undefined): DdpCountryConfig | null => {
  if (!countryCode) {
    return null;
  }
  return DDP_COUNTRY_CONFIG[countryCode.toUpperCase()] ?? null;
};

/**
 * Check if a country is DDP-eligible
 */
export const isDdpEligibleCountry = (countryCode: string | null | undefined): boolean => {
  return getDdpCountryConfig(countryCode) !== null;
};

/**
 * Get all DDP-eligible country codes
 */
export const getDdpEligibleCountries = (): string[] => {
  return Object.keys(DDP_COUNTRY_CONFIG);
};

/**
 * Get all carriers eligible for DDP in a specific country
 */
export const getDdpCarriersForCountry = (countryCode: string | null | undefined): string[] => {
  const config = getDdpCountryConfig(countryCode);
  return config?.carriers ?? [];
};

/**
 * Get walleted carriers for DDP in a specific country (used for guaranteed DDP blocking)
 */
export const getDdpWalletedCarriersForCountry = (countryCode: string | null | undefined): string[] => {
  const config = getDdpCountryConfig(countryCode);
  return config?.walletedCarriers ?? [];
};

/**
 * Get feature flag declaration for a country's DDP
 */
export const getDdpFeatureFlag = (countryCode: string | null | undefined): FeatureFlagDeclaration | null => {
  const config = getDdpCountryConfig(countryCode);
  return config?.featureFlag ?? null;
};

/**
 * Get terms label for a country
 */
export const getDdpTermsLabel = (countryCode: string | null | undefined): string | null => {
  const config = getDdpCountryConfig(countryCode);
  return config?.termsLabel ?? null;
};

/**
 * Get full terms URL for a country
 */
export const getDdpTermsUrl = (countryCode: string | null | undefined): string | null => {
  const termsLabel = getDdpTermsLabel(countryCode);
  if (!termsLabel) {
    return null;
  }
  return `${BASE_TERMS_URL}${termsLabel}`;
};

/**
 * Get introduction modal messages for a country
 */
export const getDdpIntroductionMessages = (countryCode: string | null | undefined): DdpIntroductionMessages | null => {
  const config = getDdpCountryConfig(countryCode);
  return config?.introductionMessages ?? null;
};

/**
 * Get terms modal messages for a country
 */
export const getDdpTermsMessages = (countryCode: string | null | undefined): DdpTermsMessages | null => {
  const config = getDdpCountryConfig(countryCode);
  return config?.termsMessages ?? null;
};

/**
 * Get snooze duration in hours for a country
 */
export const getDdpSnoozeDurationHours = (countryCode: string | null | undefined): number => {
  const config = getDdpCountryConfig(countryCode);
  return config?.snoozeDurationHours ?? DEFAULT_SNOOZE_DURATION_HOURS;
};
